(() => {
  const rootSelector = '.review-wall';
  const cardSelector = '.review-wall__card';

  const initialise = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.reviewWallEditorReady) return;

    const cards = Array.from(root.querySelectorAll(cardSelector));
    const prevButton = root.querySelector('[data-review-wall-prev]');
    const nextButton = root.querySelector('[data-review-wall-next]');
    const viewport = root.querySelector('[data-review-wall-viewport]');

    if (!cards.length || !prevButton || !nextButton || !viewport) return;

    if (cards.some((card) => card.classList.contains('is-active'))) {
      root.dataset.reviewWallEditorReady = 'native';
      return;
    }

    root.dataset.reviewWallEditorReady = 'fallback';

    let activeIndex = 0;
    let pointerId = null;
    let startX = 0;
    let currentX = 0;
    let dragging = false;

    const normalise = (index) => (index + cards.length) % cards.length;

    const signedOffset = (index) => {
      let offset = index - activeIndex;
      const half = cards.length / 2;

      if (offset > half) offset -= cards.length;
      if (offset < -half) offset += cards.length;

      return offset;
    };

    const updateTrackHeight = () => {
      const activeCard = cards[activeIndex];
      if (!activeCard) return;
      root.style.setProperty('--review-wall-track-height', `${activeCard.offsetHeight}px`);
    };

    const updateCards = () => {
      cards.forEach((card, index) => {
        const offset = signedOffset(index);

        card.classList.remove('is-active', 'is-before', 'is-after', 'is-far-before', 'is-far-after');
        card.setAttribute('aria-hidden', Math.abs(offset) > 1 ? 'true' : 'false');

        if (offset === 0) card.classList.add('is-active');
        else if (offset === -1) card.classList.add('is-before');
        else if (offset === 1) card.classList.add('is-after');
        else if (offset < -1) card.classList.add('is-far-before');
        else card.classList.add('is-far-after');
      });

      window.requestAnimationFrame(updateTrackHeight);
    };

    const setActive = (index) => {
      activeIndex = normalise(index);
      updateCards();
    };

    const goPrevious = () => setActive(activeIndex - 1);
    const goNext = () => setActive(activeIndex + 1);

    prevButton.addEventListener('click', goPrevious);
    nextButton.addEventListener('click', goNext);

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    });

    cards.forEach((card, index) => {
      card.addEventListener('click', () => {
        const offset = signedOffset(index);
        if (offset < 0) goPrevious();
        else if (offset > 0) goNext();
      });

      card.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', updateTrackHeight, { once: true });
        image.addEventListener('error', updateTrackHeight, { once: true });
      });
    });

    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      currentX = startX;
      dragging = true;
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture(pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== pointerId) return;

      currentX = event.clientX;
      const dragDistance = currentX - startX;
      root.style.setProperty('--review-wall-drag-x', `${dragDistance * 0.18}px`);
    });

    const finishDrag = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;

      const dragDistance = currentX - startX;
      dragging = false;
      viewport.classList.remove('is-dragging');
      root.style.setProperty('--review-wall-drag-x', '0px');

      try {
        viewport.releasePointerCapture(pointerId);
      } catch (error) {
        // The pointer can already be released when the editor replaces the section.
      }

      pointerId = null;

      if (Math.abs(dragDistance) > 42) {
        if (dragDistance < 0) goNext();
        else goPrevious();
      }
    };

    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    window.addEventListener('resize', updateTrackHeight, { passive: true });

    updateCards();
  };

  const scan = (scope = document) => {
    if (scope instanceof Element && scope.matches(rootSelector)) initialise(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initialise);
  };

  const start = () => scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    scan(event.target.closest?.('.shopify-section') || event.target);
  });
})();
